<script>
	import { onMount, onDestroy, afterUpdate } from "svelte";
	import { fade } from "svelte/transition";
	import uuid4 from "uuid4";

	import PageSection from "../../components/PageSection.svelte";
	import Select from "../../components/Select.svelte";
	import RadioGroup from "../../components/RadioGroup.svelte";
	import DropZone from "../../components/DropZone.svelte";
	import NavigationButtons from "../../components/NavigationButtons.svelte";
	import DatePicker from "../../components/DatePicker.svelte";
	import lookupService from "../../services/lookupService";

	import {
		acceptedFileUploadList,
		roleClassificationType,
		workTypeOptions,
		nsaType,
		projectSettings,
		yesNoOptions,
		yesNoUnsureOptions
	} from "../../config";
	import { pageNumber, model } from "../../stores";

	import AutocompleteInput from "../../components/AutocompleteInput.svelte";

	export let title = null;
	export let pageIndex = null;
	export let pageTitle = null;
	export let nextPage = null;
	export let previousPage = null;
	export let persistUrl = null;
	export let lastVisitedPage = null;
	export let disabled = null;

	let localStoreList = [];

	if (window.windowStoreList) {
		localStoreList = window.windowStoreList;
	} else if ($lookupService) {
		localStoreList = [...$lookupService];
		window.windowStoreList = localStoreList;
	}

	let startDate = new Date(2014, 8, 1);
	if (!disabled) {
		pageNumber.update((p) => pageIndex);
		$model.LastPageVisited = lastVisitedPage;
		document.title = pageTitle;
	}

	const basepath = window.basePath;
	let dropzoneId = "dz";
	let required = true;
	let options = {
		url: "/api/file-upload?token=" + $model.RegistrationToken,
		acceptedFiles: acceptedFileUploadList
	};

	$model.payslipDocumentList = $model.payslipDocumentList || [];
	$model.corDocumentList = $model.corDocumentList || [];
	$model.othDocumentList = $model.othDocumentList || [];

	function unformatDate(formattedDate) {
		if (!formattedDate || typeof formattedDate === "object") return formattedDate;

		const components = formattedDate.split("/");
		return new Date(components[2], components[1] - 1, components[0]);
	}

	$: finishWorkStartDate = unformatDate($model.DateStart);

	if (!$model.StoreAllocations) {
		$model.StoreAllocations = [];
	}

	// typeahead list logic
	$: {
		if ($model.MostRecentStore && !$model.MostRecentStore.CustomName && !disabled) {
			const mostRecentStoreId = $model.MostRecentStore ? $model.MostRecentStore.LookupStoreId : null;
			if (mostRecentStoreId && mostRecentStoreId !== $model.MostRecentStoreId) {
				$model.MostRecentStoreId = mostRecentStoreId;
				$model.MostRecentStoreCustomName = null;
			}
		}
	}

	$: {
		if ($model.MostRecentStore && $model.MostRecentStore.CustomName && !disabled) {
			const mostRecentStoreId = $model.MostRecentStore ? $model.MostRecentStore.LookupStoreId : null;
			if (mostRecentStoreId && mostRecentStoreId !== $model.MostRecentStoreId) {
				$model.MostRecentStoreId = null;
				$model.MostRecentStoreCustomName = $model.MostRecentStore.CustomName;
			}
		}
	}

	$: {
		if (
			$model.TemporaryStoreAllocations &&
			!disabled &&
			$model.TemporaryStoreAllocations.length !== $model.StoreAllocations.length
		) {
			$model.StoreAllocations = [
				...$model.TemporaryStoreAllocations.map((store) =>
					store.CustomName ? { ...store, LookupStoreId: null } : store
				)
			];
		} else if (
			!$model.TemporaryStoreAllocations &&
			!disabled &&
			$model.StoreAllocations &&
			$model.StoreAllocations.length
		) {
			$model.StoreAllocations = [];
		}
	}

	const handleCreate = (newStore) => {
		const newStoreObject = { StoreName: newStore, LookupStoreId: uuid4(), CustomName: newStore };
		localStoreList.unshift(newStoreObject);
		localStoreList = localStoreList;
		window.windowStoreList = localStoreList;

		return newStoreObject;
	};

	const handleRecentBlur = async (inputId) => {
		if (disabled) return false;

		await new Promise((resolve) => setTimeout(resolve, 200));

		const inputElement = document.getElementById(inputId);

		if (!inputElement) return false;

		const inputValue = inputElement.value;

		if (!inputValue) return false;

		const localStore = localStoreList.find((item) => item.StoreName === inputValue);

		if (localStore) {
			$model.MostRecentStore = localStore;
		} else {
			const newStore = handleCreate(inputValue);
			$model.MostRecentStore = newStore;
		}
	};

	const handleAllocationBlur = async (inputId) => {
		if (disabled) return false;

		await new Promise((resolve) => setTimeout(resolve, 200));

		const inputElement = document.getElementById(inputId);

		if (!inputElement) return false;

		const inputValue = inputElement.value;

		if (!inputValue) return false;

		let newStore = localStoreList.find((item) => item.CustomName === inputValue);

		if (!newStore) {
			newStore = handleCreate(inputValue);
		}

		const newStoreAllocations = $model.TemporaryStoreAllocations || [];

		if (!newStoreAllocations.find((item) => item.StoreName === newStore)) {
			$model.TemporaryStoreAllocations = [...($model.TemporaryStoreAllocations || []), newStore];
		}
	};
</script>

<PageSection>
	<h2>Details</h2>

	<form novalidate autocomplete="off">
		{#if !disabled}
			<p>The following section is required to be completed.</p>
			<p>
				If you are unable to complete any part of this section of the form, we are here to assist you. Please
				contact us on {projectSettings.phoneNumber} or via email to
				<a href="mailto:{projectSettings.emailAddress}" target="_blank" rel="noreferrer noopener"
					>{projectSettings.emailAddress}</a
				>.
			</p>
		{/if}
		<div class="form-row">
			<div class="form-group col-md-12">
				<label class:isrequired={!disabled}>Did you or do you work at a McDonald's outlet?</label>
				<RadioGroup
					{disabled}
					name="workType"
					bind:value={$model.McDonaldsWorkType}
					optionList={workTypeOptions}
					{required}
				></RadioGroup>
				<div class="radio-error"></div>
			</div>
		</div>

		{#if disabled}
			<div class="form-row">
				<div class="form-group col-md-12">
					<label for="otherOutlets"
						>What is the most recent or current McDonald's Outlet Location you have worked at?</label
					>
					<div>
						<ul>
							<li>{$model.MostRecentStore.StoreName}</li>
						</ul>
					</div>
				</div>
			</div>
			<div class="form-row">
				<div class="form-group col-md-12">
					<label for="otherOutlets">What other McDonald's Outlet Locations have you worked at?</label>
					<div>
						{#if $model.TemporaryStoreAllocations && $model.TemporaryStoreAllocations.length}
							<ul>
								{#each $model.TemporaryStoreAllocations as outlet}
									<li>{outlet.StoreName}</li>
								{/each}
							</ul>
						{:else}
							None supplied
						{/if}
					</div>
				</div>
			</div>
		{:else}
			<div class="form-row">
				<div class="form-group col-md-12">
					<AutocompleteInput
						items={localStoreList}
						bind:selectedItem={$model.MostRecentStore}
						id="mostRecentOutlet"
						create={true}
						onCreate={handleCreate}
						label={`What is the most recent or current McDonald's Outlet Location you have worked at?`}
						{disabled}
						{required}
						labelFieldName={"StoreName"}
						valueFieldName={"LookupStoreId"}
						onBlur={() => handleRecentBlur("mostRecentOutlet")}
					/>
				</div>
			</div>
			<div class="form-row">
				<div class="form-group col-md-12">
					<AutocompleteInput
						items={localStoreList}
						bind:selectedItem={$model.TemporaryStoreAllocations}
						id="otherOutlets"
						multiple={true}
						create={true}
						onCreate={handleCreate}
						label={`What other McDonald's Outlet Locations have you worked at?`}
						{disabled}
						labelFieldName={"StoreName"}
						valueFieldName={"LookupStoreId"}
						onBlur={() => handleAllocationBlur("otherOutlets")}
					/>
				</div>
			</div>
		{/if}

		<div class="form-row">
			<div class="form-group col-md-12">
				<label for="dateStart" class:isrequired={!disabled}
					>Approximately when did you start working at a McDonald's outlet?</label
				>
				<DatePicker
					openOnClass="calendarpop"
					bind:formattedSelected={$model.DateStart}
					{disabled}
					{required}
					placeholder="e.g. 01/09/2020"
					validator="true"
					id="dateStart"
				/>
			</div>
		</div>

		<div class="form-row">
			<div class="form-group col-md-12">
				<label class:isrequired={!disabled}>Do you still work at McDonald's?</label>
				<RadioGroup
					name="stillWorking"
					bind:value={$model.StillWorking}
					optionList={yesNoOptions}
					{disabled}
					{required}
				></RadioGroup>
				<div class="radio-error"></div>
			</div>
		</div>

		{#if $model.StillWorking == "No"}
			<div class="form-row">
				<div class="form-group col-md-12">
					<label class:isrequired={!disabled} for="dateEnd"
						>Approximately when did you finish working at a McDonald's outlet?</label
					>
					<DatePicker
						openOnClass="calendarpop"
						bind:formattedSelected={$model.DateEnd}
						{disabled}
						{required}
						start={finishWorkStartDate || new Date(1980, 1, 1)}
						end={new Date()}
						placeholder="e.g. 01/09/2020"
						validator="true"
						id="dateEnd"
					/>
				</div>
			</div>
		{/if}
		<div class="form-row">
			<div class="form-group col-md-12">
				<label for="averageShiftsPerWeek" class:isrequired={!disabled}
					>On average how many shifts per week did you work at McDonald's?</label
				>
				<input
					{disabled}
					required
					type="number"
					min="0"
					id="averageShiftsPerWeek"
					bind:value={$model.AverageShiftsPerWeek}
					class="form-control"
				/>
			</div>
		</div>

		<div class="form-row">
			<div class="form-group col-md-12">
				<label class:isrequired={!disabled}>Were the shifts generally 4 hours or greater?</label>
				<RadioGroup
					{disabled}
					name="greaterThanFourHours"
					bind:value={$model.GreaterThanFourHours}
					optionList={yesNoUnsureOptions}
					{required}
				></RadioGroup>
				<div class="radio-error"></div>
			</div>
		</div>

		<div class="form-row">
			<div class="form-group col-md-12">
				<label class:isrequired={!disabled}>
					When working at a McDonald's Restaurant on a 4 hour or longer shift did you ever receive a Paid 10
					Minute Rest Break?
				</label>
				<RadioGroup
					{disabled}
					name="paidRestBreakType"
					bind:value={$model.PaidRestBreakType}
					optionList={yesNoUnsureOptions}
					{required}
				></RadioGroup>
				<div class="radio-error"></div>
			</div>
		</div>

		<div class="form-row">
			<div class="form-group col-md-12">
				<label class:isrequired={!disabled}>
					When working at a McDonald's Restaurant on a 4 hour or longer shift did you ever receive a paid
					drinks break?
				</label>
				<RadioGroup
					{disabled}
					name="paidDrinkBreak"
					bind:value={$model.PaidDrinkBreak}
					optionList={nsaType}
					{required}
				></RadioGroup>
				<div class="radio-error"></div>
			</div>
		</div>

		<div class="form-row">
			<div class="form-group col-md-12">
				<label class:isrequired={!disabled}>Do you have a payslip available from your McDonald's work?</label>
				<RadioGroup
					{disabled}
					name="paySlipAvailable"
					bind:value={$model.PaySlipAvailable}
					optionList={yesNoOptions}
					{required}
				></RadioGroup>
				<div class="radio-error"></div>
			</div>
		</div>

		{#if $model.PaySlipAvailable == "Yes"}
			<div class="form-row">
				<!-- <div class="form-row" transition:slide> -->
				<div class="form-group col-md-12">
					<label>
						Please upload your most recent McDonald's payslips, if you have one available. Please also
						provide other supporting documentation, if available.
					</label>
					<h3>Payslip</h3>
					<DropZone
						{disabled}
						options={{
							...options,
							renameFile: (file) => `PAYSLIP-${file.name}`
						}}
						id="dz-payslip"
						message="Drop files here or click to upload."
						bind:documentList={$model.payslipDocumentList}
					></DropZone>
					<h3>Correspondence</h3>
					<DropZone
						{disabled}
						options={{
							...options,
							renameFile: (file) => `COR-${file.name}`
						}}
						id="dz-cor"
						message="Drop files here or click to upload."
						bind:documentList={$model.corDocumentList}
					></DropZone>
					<h3>Other</h3>
					<DropZone
						{disabled}
						options={{
							...options,
							renameFile: (file) => `OTH-${file.name}`
						}}
						id="dz-oth"
						message="Drop files here or click to upload."
						bind:documentList={$model.othDocumentList}
					></DropZone>
				</div>
			</div>
		{/if}

		<div class="form-row">
			<div class="form-group col-md-12">
				<label for="roleClassification" class:isrequired={!disabled}
					>Which classification most closely describes your most senior role with McDonald's?</label
				>
				<Select
					{disabled}
					{required}
					id="roleClassification"
					bind:value={$model.RoleClassification}
					optionList={roleClassificationType}
					textProperty="name"
					valueProperty="name"
				></Select>
			</div>
		</div>

		<div class="form-row">
			<div class="form-group col-md-12">
				<label for="otherComments">Is there anything else you want to tell us?</label>
				<textarea {disabled} rows="4" id="otherComments" bind:value={$model.OtherComments} class="form-control"
				></textarea>
			</div>
		</div>

		{#if !disabled}
			<div class="form-row">
				<div class="col-md-12 last">
					<NavigationButtons {persistUrl} {previousPage} {nextPage} tealiumTag={true} pageName={title}
					></NavigationButtons>
				</div>
			</div>
		{/if}
	</form>
</PageSection>
